<script>
import { DateTime } from 'luxon'
import { nextTick } from 'vue'
import useService from '../../../../Composables/Service/useService.ts'
import useServiceForm from '../../../../Composables/Service/useServiceForm.ts'
import { globalVariablesMixin } from '../../Mixins/global'
import mixinComponent from '../../Mixins/mixinComponent'
import Alert from '../UI/Alert.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import FormCreator from '../UI/Form/FormCreator.vue'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import Opteven from './External/Opteven.vue'
import ServiceLevelForm from './ServiceLevelForm.vue'

export default {
  components: { ServiceLevelForm, Alert, ButtonAll, FormCreator, FieldCreator, ModalDialog, Opteven },
  mixins: [mixinComponent, globalVariablesMixin],
  props: {
    disabled: { type: Boolean, default: false },
    financeId: { type: Number, required: true },
    serviceId: { type: Number, default: null, required: false },
    serviceTypeId: { type: Number, default: null, required: false },
    method: { type: String, required: false },
    editButton: { type: Boolean, default: false },
    hasLevel: { type: Boolean, default: false },
  },

  data() {
    return {
      params: {},
      sending: false,
      loading: false,
      loadModal: false,
      loadForm: false,
      loadField: false,
      amountBase: 0,
      majoration: 0,
      formData: null,
    }
  },

  computed: {
    modalId() {
      if (this.method != 'delete') {
        if (this.serviceId) {
          return this.serviceId
        }

        return this.financeId
      }

      return 'delete' + this.financeId
    },
    api() {
      if (this.loadForm && this.loadField && this.$refs.myformservice?.object?.others) {
        return this.$refs.myformservice.object.others.api
      }

      return null
    },
    formSubmitDisabled() {
      const hasServiceType = this.formData && this.formData.datas && this.formData.datas.serviceType
      const isEditingExistingService = this.method === 'patch' && this.serviceId

      return this.disabled || !(hasServiceType || isEditingExistingService) || this.hasMissingTerms
    },
    isDisabled() {
      return this.disabled && this.method !== 'patch'
    },
  },

  methods: {
    updateData(newData) {
      this.formData = newData
    },

    globalBuild({ def, loading, build, object: service }) {
      this.$refs['service-' + this.modalId].open()

      this.def = def
      this.service = service
      this.amountBase = service.amountBase
      this.majoration = service.majoration
      this.loading = loading
      this.loadField = build
      this.$emit('service-post', service)
    },
    isSending(bool) {
      this.sending = bool
    },
    showForm() {
      if (this.serviceId) {
        this.params = { financeId: this.financeId, id: this.serviceId }
        this.loadForm = true
      } else {
        if (this.serviceTypeId) {
          this.params = { financeId: this.financeId, serviceType: this.serviceTypeId }
          this.loadModal = true
          this.postJSON('services', this.params).then(
            (json) => {
              this.serviceId = json.id
              this.params = { financeId: this.financeId, id: this.serviceId }
              this.loadForm = true
              this.$emit('service-post', json)
              this.loadModal = false
              //this.setValue(this.serviceId,0,json.id);
            },
            (reason) => {
              alert(reason)
            },
          )
        } else {
          this.params = { financeId: this.financeId }
          this.loadForm = true
        }
      }
    },
    closeModal() {
      this.$refs['service-' + this.modalId].close(false)

      this.loadForm = false
      this.loadField = false
    },
    setDatas(json) {
      this.service = json
      this.$emit('services-sent', { json, method: this.method })

      if (['post', 'patch'].includes(this.method)) {
        this.closeModal()
        return
      }

      this.loadForm = false
      this.loadField = false
      this.$nextTick(() => {
        this.loadForm = true
      })
    },
    setDatasFromRefresh(json) {
      this.service = json
      this.$emit('services-sent', { json: json, method: this.method })
    },
    getAmount(val) {
      this.amountBase = this.$root.getNumber(val)
      if (this.service.amount && this.majoration) {
        this.amountBase = this.amountBase * (this.majoration / 100 + 1)
      }

      this.service.amount = this.$root.getRoundFloat(this.amountBase, 1, 100)
    },
    getMajoration(val) {
      this.majoration = this.$root.getNumber(val)
      if (this.service.amount && this.amountBase) {
        let amountBase = this.amountBase * (this.majoration / 100 + 1)

        this.service.amount = this.$root.getRoundFloat(amountBase, 1, 100)
      }
    },
    onFormError(error) {
      if (error.response.status === 400 && error.response.code !== 'form.validation.error') {
        this.closeModal()
        this.$emit('not-eligible')
      }
    },
    validateService(datas, callback) {
      if (!datas.startAt || !datas.endAt) {
        return
      }

      const errors = []

      const startAt = DateTime.fromISO(datas.startAt)
      const endAt = DateTime.fromISO(datas.endAt)

      if (startAt > endAt) {
        errors.push({ fieldName: 'endAt', message: 'service.fields.endAt.errors.outOfRange' })
      }

      callback(errors)
    },
    submit() {
      this.$refs.myformservice.methods()
    },
    async onServiceTypeChanged(serviceType) {
      if (serviceType) {
        this.params = { ...this.params, serviceTypeId: serviceType }
      }

      await this.$nextTick()
      await this.$refs.myformservice?.methods('get')
      delete this.params['serviceTypeId']
    },
  },
  setup() {
    const { service, hasMissingTerms } = useService()
    const { def, hasIncreaseRateField, disabledStartDates, disabledEndDates } = useServiceForm({ service: service })

    return { service, def, hasIncreaseRateField, disabledStartDates, disabledEndDates, hasMissingTerms }
  },
  watch: {
    'service.level': {
      handler(value) {
        if (!this.formData) {
          return
        }

        if (!value) {
          delete this.formData.datas.levels
        }
      },
    },
  },
}
</script>
<template>
  <div class="service-form">
    <i v-if="editButton && !loadModal && !loading" class="fa fa-edit"></i>
    <div
      v-else-if="editButton && (loadModal || loading)"
      class="d-flex justify-content-center align-items-center flex-fill"
    >
      <div class="spinner-border" style="width: 1rem; height: 1rem" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <a
      v-else
      :class="isDisabled ? 'cursor-disabled' : 'cursor-pointer'"
      role="button"
      @[!isDisabled&&`click`]="showForm"
    >
      <i
        v-if="method == 'post'"
        :class="'fa fa-plus' + (isDisabled ? ' filter-disabled-2' : '')"
        e2e-button="serviceAdd"
      />
      <i
        v-else-if="method == 'patch'"
        :class="'fa fa-edit' + (isDisabled ? ' filter-disabled-4' : '')"
        e2e-button="serviceUpdate"
      />
      <i v-else :class="'fas fa-trash' + (isDisabled ? ' filter-disabled-4' : '')" e2e-button="serviceDelete" />
      {{ method == 'post' ? $t('service.add') : '' }}
    </a>

    <ModalDialog
      :ref="'service-' + modalId"
      :id="'service-' + modalId"
      id-modal-page="service"
      class-modal-dialog="modal-xl"
      :title="service?.serviceType?.label ?? ''"
      data-test-id="service-form"
      e2e-modal="service"
      @icon-close="closeModal"
    >
      <div v-if="loading" class="row justify-content-center">
        <div class="lds-ellipsis">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
      <template v-if="loadField">
        <FieldCreator
          :form-data="formData"
          name="serviceType"
          form-ref="myformservice"
          domain="service"
          label-top
          :disabled="disabled"
          @get-select-change="onServiceTypeChanged"
        />
        <FieldCreator
          :form-data="formData"
          v-if="service.serviceType"
          name="equipment"
          form-ref="myformservice"
          domain="service"
          label-top
          :disabled="disabled"
        />
        <div class="row">
          <div :class="{ 'col-12': true, 'col-md-6': method === 'patch' }">
            <FieldCreator
              v-if="service.serviceType"
              :form-data="formData"
              name="supplier"
              form-ref="myformservice"
              domain="service"
              label-top
              :disabled="disabled"
            />
          </div>
          <div class="col-12 col-md-6">
            <FieldCreator
              :form-data="formData"
              v-if="method === 'patch' && !isTheme('yneia')"
              name="contractNumber"
              form-ref="myformservice"
              domain="service"
              label-top
              :disabled="disabled"
            />
          </div>
        </div>
        <FieldCreator
          :form-data="formData"
          v-if="method === 'patch'"
          name="amountBase"
          form-ref="myformservice"
          domain="service"
          label-top
          @get-input-change="getAmount"
          :disabled="disabled"
        />
        <FieldCreator
          :form-data="formData"
          v-if="method === 'patch'"
          name="majoration"
          form-ref="myformservice"
          domain="service"
          label-top
          @get-input-change="getMajoration"
          :disabled="disabled"
        />
        <div class="row">
          <div v-if="!service.level" class="col">
            <FieldCreator
              :form-data="formData"
              v-if="method === 'patch'"
              name="amount"
              form-ref="myformservice"
              domain="service"
              label-top
              not-remove
              :disabled="disabled"
            />
          </div>

          <div v-if="hasIncreaseRateField" class="col">
            <FieldCreator
              v-if="method === 'patch'"
              :form-data="formData"
              name="increaseRate"
              form-ref="myformservice"
              domain="service"
              label-top
            />
          </div>
        </div>

        <div v-if="method === 'patch' && !isTheme('yneia')" class="row">
          <div class="col-12 col-md-6">
            <FieldCreator
              v-model="service.startAt"
              :form-data="formData"
              name="startAt"
              form-ref="myformservice"
              type-value="string"
              domain="service"
              label-top
              :disabled="disabled"
              :disabled-options="disabledStartDates"
            />
          </div>
          <div class="col-12 col-md-6">
            <FieldCreator
              v-model="service.endAt"
              :form-data="formData"
              name="endAt"
              form-ref="myformservice"
              type-value="string"
              domain="service"
              label-top
              :disabled="disabled"
              :disabled-options="disabledEndDates"
            />
          </div>
        </div>
        <FieldCreator
          v-if="method === 'patch'"
          :form-data="formData"
          name="includeToRent"
          form-ref="myformservice"
          domain="service"
          label-top
          :disabled="disabled"
        />
        <FieldCreator
          v-if="method === 'patch' && !isTheme('yneia')"
          v-model="service.level"
          :form-data="formData"
          name="level"
          form-ref="myformservice"
          domain="service"
          label-top
          :disabled="disabled"
        />
        <div v-if="method === 'patch'" v-for="(index, name) in def.others">
          <FieldCreator
            :form-data="formData"
            :name="'others.' + name"
            form-ref="myformservice"
            domain="service"
            type-value="string"
            label-top
            dynamic-label
            :placeholder-config="false"
            :disabled="disabled"
          />
        </div>
        <FieldCreator
          :form-data="formData"
          v-if="method === 'patch'"
          name="includedToRent"
          form-ref="myformservice"
          domain="service"
          label-top
          :disabled="disabled"
        />

        <template v-if="service.level">
          <ServiceLevelForm :service="service" :form="formData" />
        </template>
      </template>

      <div v-if="loadForm && api">
        <component :is="api.label" :service="$refs.myformservice.object"></component>
      </div>

      <div class="my-3">
        <FormCreator
          v-if="loadForm"
          ref="myformservice"
          endpoint="services"
          :id="serviceId"
          :method="method"
          :params="params"
          class-to-add="d-none"
          in-modal
          data-test-id="service-send-button"
          @global-build="globalBuild"
          @refresh-return="setDatasFromRefresh"
          @form-return="setDatas"
          @is-sending="isSending"
          @validate="validateService"
          @error="onFormError"
          @update-data="updateData"
        />

        <ButtonAll
          type="validation"
          size="large"
          margin="no"
          label="button.validate"
          :disabled="formSubmitDisabled"
          :loading="sending"
          @click="submit"
        />

        <ButtonAll
          type="secondary"
          size="large"
          margin="no"
          label="main.close"
          class="ml-3"
          e2e-button="close"
          @click="closeModal"
        />
      </div>
    </ModalDialog>
  </div>
</template>
